import { useFormik } from 'formik';
import React from 'react';
import { Redirect, useHistory } from 'react-router-dom';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useNotifications } from 'modules/app/notifications';
import {
  getDefaultEmployeeAccountName,
  useIntegrationStatusQuery,
} from 'modules/bookkeep';
import { canAccessEmployeeAccountsSettings } from 'modules/bookkeep/settings/integrations/helper/settings-access';
import { useGetDefaultEmployeeAccountQuery } from 'modules/bookkeep/settings/integrations/hooks/useGetDefaultEmployeeAccountQuery';
import { useSetDefaultEmployeeAccountMutation } from 'modules/bookkeep/settings/integrations/hooks/useSetDefaultEmployeeAccountMutation';
import { ExpenseAccountsSkeletonTable } from 'modules/bookkeep/settings/integrations/pages/LegacyIntegrationsAccountingPage/sections/ExpenseAccountsSection/ExpenseAccountsSkeletonTable';
import { TaskStepLayout } from 'modules/onboarding/components/TaskStepLayout';
import { DefaultEmployeeAccount } from 'modules/onboarding/setup-hub/pages/Accounting/AccountingEmployeeAccounts/components/DefaultEmployeeAccount';
import { EmployeeAccountsTable } from 'modules/onboarding/setup-hub/pages/Accounting/AccountingEmployeeAccounts/components/EmployeeAccountsTable';
import { useAccountingCountry } from 'modules/onboarding/setup-hub/pages/Accounting/hooks/useAccountingCountry';
import { useUpdateCachedTaskStatus } from 'modules/onboarding/setup-hub/pages/SetupHubPage/hooks/useUpdateCachedTaskStatus';
import { routes } from 'modules/onboarding/setup-hub/pages/routes';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { routeFor } from 'src/core/constants/routes';

import type { FormValues } from './form';

export const AccountingEmployeeAccounts = () => {
  const { t } = useTranslation('global');
  const companyId = useCompanyId();
  const history = useHistory();
  const { dangerNotif } = useNotifications();

  const getDefaultEmployeeAccountsQuery = useGetDefaultEmployeeAccountQuery({
    isEnabled: true,
  });
  const [setDefaultEmployeeAccount] = useSetDefaultEmployeeAccountMutation();
  const defaultEmployeeAccount = unwrapQuery(getDefaultEmployeeAccountsQuery);

  const integrationStatusQueryState = useIntegrationStatusQuery();
  const accountingCountry = useAccountingCountry();

  const updateCachedTaskStatus = useUpdateCachedTaskStatus();

  const { values, handleChange, handleSubmit } = useFormik<FormValues>({
    initialValues: {
      useDefaultValue: Boolean(
        !defaultEmployeeAccount?.isArchived &&
          !!defaultEmployeeAccount?.isDefault,
      ),
      defaultEmployeeAccount: defaultEmployeeAccount?.generalAccountCode,
    },
    validate: (newValues) => {
      if (
        newValues.useDefaultValue &&
        !newValues.defaultEmployeeAccount?.trim()?.length
      ) {
        return { defaultEmployeeAccount: t('misc.requiredField') };
      }
    },
    enableReinitialize: true,
    onSubmit: async (newValues) => {
      try {
        const newDefaultEmployeeAccountCode =
          values.defaultEmployeeAccount?.trim();
        // If defaultEmployeeAccountCode is set, we set the default employee account
        if (
          newValues.useDefaultValue &&
          newDefaultEmployeeAccountCode?.length
        ) {
          const result = await setDefaultEmployeeAccount({
            ...defaultEmployeeAccount,
            isArchived: false,
            auxiliaryAccountCode: defaultEmployeeAccount?.auxiliaryAccountCode,
            name:
              defaultEmployeeAccount?.name ||
              getDefaultEmployeeAccountName(accountingCountry),
            generalAccountCode: newDefaultEmployeeAccountCode,
          });
          if (result.outcome === 'notSet') {
            dangerNotif(
              result.reason === 'codeAlreadyExists'
                ? t(
                    'bookkeep.integrations.settings.employeeAccountsTable.codeAlreadyExistsError',
                  )
                : t('misc.errors.unknownError'),
            );
            return;
          }
        }
        // If the default employee account was not archived
        // and we are not setting any value, then we archive it (otherwise it's already archived, nothing to update)
        if (
          !newValues.useDefaultValue &&
          defaultEmployeeAccount &&
          !defaultEmployeeAccount.isArchived
        ) {
          const result = await setDefaultEmployeeAccount({
            ...defaultEmployeeAccount,
            isArchived: true,
          });
          if (result.outcome === 'notSet') {
            dangerNotif(t('misc.errors.unknownError'));
            return;
          }
        }
        updateCachedTaskStatus('employee_account_codes', 'setup_hub');
        goBackToHub();
      } catch {
        return dangerNotif(
          t(
            'bookkeep.integrations.settings.employeeAccountsTable.editDefaultAccountFailure',
          ),
        );
      }
    },
  });

  const goBackToHub = () => {
    const previousPath = routeFor(routes.SETUP_HUB_TASK_LIST.path, {
      company: companyId,
    });
    history.push(previousPath);
  };

  const getPageContent = () => {
    if (integrationStatusQueryState.status === 'loading') {
      return <ExpenseAccountsSkeletonTable />;
    }
    if (
      integrationStatusQueryState.status === 'error' ||
      !canAccessEmployeeAccountsSettings(integrationStatusQueryState.data)
    ) {
      dangerNotif(t('misc.errors.unknownError'));
      return (
        <Redirect
          to={routeFor(routes.SETUP_HUB_TASK_LIST.path, { company: companyId })}
        />
      );
    }
    return (
      <>
        <EmployeeAccountsTable
          useDefaultValue={values.useDefaultValue}
          integrationStatus={integrationStatusQueryState.data}
        />
        <DefaultEmployeeAccount values={values} handleChange={handleChange} />
      </>
    );
  };

  return (
    <div className="page__container">
      <TaskStepLayout
        description={t('setupHub.employeeAccounts.description')}
        isLastStep
        onBack={goBackToHub}
        onNext={handleSubmit}
        title={t('setupHub.employeeAccounts.title')}
        video={{
          // TODO(GROW-1484): Replace with localized video URL
          url: '',
          title: t('misc.needHelp'),
        }}
      >
        {getPageContent()}
      </TaskStepLayout>
    </div>
  );
};
